import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import { AssetErrorDetail } from '../../../services/wellControlService';

interface WellControlState {
  scanProgressCount: number;
  nodeIdStatusMap: Record<string, any>;
  apiCompletedWells: Record<string, boolean | null>;
  totalScanCount: number;
  isTableScanning: boolean;
  isTableOpen: boolean;
  scannedWellsData: Array<{
    assetId: string;
    wellName: string;
    lastGoodScan: string;
    commsStatus: string;
    command: string;
  }>;
  scanStats: {
    failedCount: number;
    successCount: number;
    processedCount: number;
    totalCount: number;
  };
  timedOutWells: Record<string, boolean>;
  failedAssets: AssetErrorDetail[];
  selectedGroupName?: string;
  actionCommand?: string;
  processingStartTimes: Record<string, number>;
}

const initialState: WellControlState = {
  scanProgressCount: 0,
  nodeIdStatusMap: {},
  apiCompletedWells: {},
  totalScanCount: 0,
  isTableScanning: false,
  isTableOpen: false,
  scannedWellsData: [],
  scanStats: {
    failedCount: 0,
    successCount: 0,
    processedCount: 0,
    totalCount: 0,
  },
  timedOutWells: {},
  failedAssets: [],
  selectedGroupName: '',
  actionCommand: 'Scan',
  processingStartTimes: {},
};

const wellControlSlice = createSlice({
  name: 'wellControl',
  initialState,
  reducers: {
    setScanProgressCount(state, action: PayloadAction<number>) {
      state.scanProgressCount = action.payload;
    },
    resetScanProgressCount(state) {
      state.scanProgressCount = 0;
    },
    incrementScanProgressCount(state) {
      state.scanProgressCount += 1;
    },
    addOrUpdateNodeIdStatus(state, action: PayloadAction<{ nodeId: string; status: string }>) {
      const normalizedNodeId = action.payload.nodeId.trim().toLowerCase();
      state.nodeIdStatusMap[normalizedNodeId] = action.payload.status;
    },
    resetNodeIdStatusMap(state) {
      state.nodeIdStatusMap = {};
    },
    markWellScanComplete(state, action: PayloadAction<{ assetId: string }>) {
      state.apiCompletedWells[action.payload.assetId] = true;
    },
    setApiCompletedWells(state, action: PayloadAction<Record<string, boolean | null>>) {
      state.apiCompletedWells = action.payload;
    },
    resetApiCompletedWells(state) {
      state.apiCompletedWells = {};
    },
    markWellScanStarted(state, action: PayloadAction<{ assetId: string }>) {
      state.apiCompletedWells[action.payload.assetId] = null;
    },
    setTotalScanCount(state, action: PayloadAction<number>) {
      state.totalScanCount = action.payload;
    },
    setIsTableScanning(state, action: PayloadAction<boolean>) {
      state.isTableScanning = action.payload;
    },
    setIsTableOpen(state, action: PayloadAction<boolean>) {
      state.isTableOpen = action.payload;
    },
    setScannedWellsData(
      state,
      action: PayloadAction<
        Array<{
          assetId: string;
          wellName: string;
          lastGoodScan: string;
          commsStatus: string;
          command: string;
        }>
      >,
    ) {
      state.scannedWellsData = action.payload;
    },
    setScanStats(
      state,
      action: PayloadAction<{ failedCount: number; successCount: number; processedCount: number; totalCount: number }>,
    ) {
      state.scanStats = action.payload;
    },
    resetScannedWellsData(state) {
      state.scannedWellsData = [];
    },
    setTimedOutWells(state, action: PayloadAction<Record<string, boolean>>) {
      state.timedOutWells = { ...state.timedOutWells, ...action.payload };
    },
    setFailedAssets(state, action: PayloadAction<AssetErrorDetail[]>) {
      state.failedAssets = action.payload;
    },
    resetFailedAssets(state) {
      state.failedAssets = [];
    },
    resetTimedOutWells(state) {
      state.timedOutWells = {};
    },
    setSelectedGroupName(state, action: PayloadAction<string>) {
      state.selectedGroupName = action.payload;
    },
    setActionCommand(state, action: PayloadAction<string>) {
      state.actionCommand = action.payload;
    },
    setProcessingStartTimes(state, action: PayloadAction<Record<string, number>>) {
      state.processingStartTimes = action.payload;
    },
    resetProcessingStartTimes(state) {
      state.processingStartTimes = {};
    },
  },
});

export const {
  setScanProgressCount,
  resetScanProgressCount,
  incrementScanProgressCount,
  addOrUpdateNodeIdStatus,
  resetNodeIdStatusMap,
  markWellScanComplete,
  markWellScanStarted,
  setApiCompletedWells,
  resetApiCompletedWells,
  setTotalScanCount,
  setIsTableScanning,
  setIsTableOpen,
  setScannedWellsData,
  setScanStats,
  resetScannedWellsData,
  setTimedOutWells,
  setFailedAssets,
  resetFailedAssets,
  resetTimedOutWells,
  setSelectedGroupName,
  setActionCommand,
  setProcessingStartTimes,
  resetProcessingStartTimes,
} = wellControlSlice.actions;

export default wellControlSlice.reducer;

export const selectProgressDisplay = (state: RootState) => {
  const { apiCompletedWells, scannedWellsData, timedOutWells } = state.wellControl;
  const assetIds =
    scannedWellsData.length > 0 ? scannedWellsData.map((well) => well.assetId) : Object.keys(apiCompletedWells);

  const totalCount = assetIds.length;

  // Only count wells that are in the current assetIds
  const failedCount = assetIds.filter((id) => apiCompletedWells[id] === false || timedOutWells[id] === true).length;
  const successCount: number = assetIds.filter((id) => apiCompletedWells[id] === true).length;
  const processedCount: number = failedCount + successCount;

  return processedCount === totalCount ? 100 : totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0;
};

export const selectScanStats = (state: RootState) => {
  const { apiCompletedWells, scannedWellsData, timedOutWells } = state.wellControl;
  const assetIds =
    scannedWellsData.length > 0 ? scannedWellsData.map((well) => well.assetId) : Object.keys(apiCompletedWells);

  const totalCount = assetIds.length;
  const successCount: number = assetIds.filter((id) => apiCompletedWells[id] === true).length;

  const combinedFailedCount = assetIds.filter(
    (id) => apiCompletedWells[id] === false || timedOutWells[id] === true,
  ).length;

  const processedCount: number = combinedFailedCount + successCount;

  return { failedCount: combinedFailedCount, successCount, processedCount, totalCount };
};
